import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'outline' | 'ghost' | 'danger' | 'soft';
type Size = 'xs' | 'sm' | 'md';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-white border-transparent hover:bg-accent-hi shadow-sm',
  outline: 'bg-surface text-ink border-line hover:bg-surface-2 hover:border-line-strong',
  ghost:   'bg-transparent text-muted border-transparent hover:bg-surface-2 hover:text-ink',
  soft:    'bg-accent-soft text-accent-ink border-transparent hover:brightness-95',
  danger:  'bg-danger-soft text-danger border-transparent hover:brightness-95',
};

const SIZE: Record<Size, string> = {
  xs: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
};

export function Button({ variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest }: Props) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center border font-semibold whitespace-nowrap select-none',
        'transition-[background-color,border-color,box-shadow,transform] duration-150 active:scale-[0.98]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-50 disabled:pointer-events-none',
        VARIANT[variant], SIZE[size], className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}

/** A square icon-only button for table rows and toolbars. */
export function IconButton({ className, title, size = 'md', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { title: string; size?: 'sm' | 'md' }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg text-muted transition-colors',
        'hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
        'disabled:opacity-40 disabled:pointer-events-none',
        size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
        className,
      )}
      {...rest}
    />
  );
}
